import React, { useCallback, useMemo, useState } from 'react';
import { FieldClass, FieldType } from '../../../../utils/apollo/graphqlSchema';
import {
  COLUMN_SELECTOR_WIDTH,
  ISelectableFieldItem,
  useAvailableSchemaFieldsForSelection,
} from './utils';
import { ReactSelectMenu } from '../../../../components/Dropdown/StyledReactSelect';
import { components, SingleValue, SingleValueProps } from 'react-select';
import { colors } from '../../../../config/styles';
import { ControlWithSearchIcon } from '../../../../components/Dropdown/StyledReactSelect/SelectUtils';

interface IProps {
  fieldKey: string | undefined;
  setFieldKey: (fieldKey: string) => void;
  viewKey?: string;
  placeholder?: string;
  fieldClassFilter?: FieldClass[];
  fieldTypeFilter?: FieldType[];
  // Field keys to *exclude*
  fieldKeysFilter?: string[];
  isDisabled?: boolean;
  isError?: boolean;
}

export const FieldSelectLabel = (
  props: SingleValueProps<ISelectableFieldItem, false>,
) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div>{data?.fieldName}</div>
    </components.SingleValue>
  );
};

const DataModelerSelectSchemaFieldKey = ({
  viewKey,
  fieldKey,
  setFieldKey,
  fieldClassFilter,
  fieldTypeFilter,
  fieldKeysFilter,
  placeholder,
  isDisabled,
  isError,
}: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const selectableItems: ISelectableFieldItem[] =
    useAvailableSchemaFieldsForSelection({ viewKey });
  const filteredItems = useMemo(
    () =>
      selectableItems.filter(
        (item) =>
          (!fieldClassFilter ||
            fieldClassFilter.indexOf(item.fieldClass) >= 0) &&
          (!fieldTypeFilter || fieldTypeFilter.indexOf(item.type) >= 0) &&
          (!fieldKeysFilter || !fieldKeysFilter.includes(item.fieldKey)) &&
          item.fieldName.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [
      fieldClassFilter,
      fieldTypeFilter,
      fieldKeysFilter,
      searchTerm,
      selectableItems,
    ],
  );
  const selectedItem = useMemo(() => {
    return selectableItems.find((fieldItem) => fieldItem.fieldKey === fieldKey);
  }, [fieldKey, selectableItems]);

  const handleChange = useCallback(
    (item: SingleValue<ISelectableFieldItem>) => {
      if (!item) {
        return;
      }
      setFieldKey(item.fieldKey);
    },
    [setFieldKey],
  );

  return (
    <ReactSelectMenu
      isMulti={false}
      isDisabled={isDisabled}
      options={filteredItems}
      getOptionLabel={(option) => option.fieldName}
      getOptionValue={(option) => option.fieldKey}
      placeholder={placeholder ?? 'Select Field'}
      value={selectedItem}
      components={{
        SingleValue: FieldSelectLabel,
        Control: ControlWithSearchIcon,
      }}
      inputValue={searchTerm}
      onInputChange={setSearchTerm}
      styles={{
        control: (base, _) => {
          return {
            ...base,
            width: COLUMN_SELECTOR_WIDTH,
            minWidth: COLUMN_SELECTOR_WIDTH,
            borderColor: isError ? colors.mainError : base.borderColor,
          };
        },
        menu: (base, _) => {
          return {
            ...base,
            width: COLUMN_SELECTOR_WIDTH,
            minWidth: COLUMN_SELECTOR_WIDTH,
          };
        },
      }}
      onChange={handleChange}
    />
  );
};

export default DataModelerSelectSchemaFieldKey;
